/**
 * EMOTO MARKETPLACE COLLECTION CONTROLLER
 * Geolocation Distance Calculation, Real-Time Dynamic Filtering & Inquiry Modal
 */
(function() {
  'use strict';

  // Fallback / standard ZIP coordinates cache
  const KNOWN_COORDS = {
    '33602': { lat: 27.9506, lng: -82.4572, city: 'Tampa', state: 'FL' },
    '33101': { lat: 25.7617, lng: -80.1918, city: 'Miami', state: 'FL' },
    '33701': { lat: 27.7676, lng: -82.6403, city: 'St. Petersburg', state: 'FL' },
    '33755': { lat: 27.9659, lng: -82.8001, city: 'Clearwater', state: 'FL' },
    '32801': { lat: 28.5383, lng: -81.3792, city: 'Orlando', state: 'FL' },
    '34236': { lat: 27.3364, lng: -82.5307, city: 'Sarasota', state: 'FL' },
    '10001': { lat: 40.7501, lng: -73.9967, city: 'New York', state: 'NY' },
    '90001': { lat: 33.9731, lng: -118.2479, city: 'Los Angeles', state: 'CA' },
    '75001': { lat: 32.9612, lng: -96.8376, city: 'Dallas', state: 'TX' }
  };

  const state = {
    userZip: localStorage.getItem('emoto_user_zip') || '33602',
    userCoords: null,
    activeFilters: {
      distance: null,
      condition: [],
      category: [],
      title: [],
      battery: [],
      brand: [],
      priceMin: null,
      priceMax: null
    },
    sort: 'nearest'
  };

  // Haversine distance in miles
  function calculateDistanceMiles(lat1, lon1, lat2, lon2) {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
    const R = 3958.8; // Radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
  }

  // Fetch coordinates for any 5-digit US ZIP code
  async function resolveZipCoords(zip) {
    const cleanZip = String(zip || '').trim().replace(/\D/g, '').slice(0, 5);
    if (!cleanZip || cleanZip.length !== 5) return null;

    if (KNOWN_COORDS[cleanZip]) return KNOWN_COORDS[cleanZip];

    const cacheKey = 'emoto_zip_cache_' + cleanZip;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        KNOWN_COORDS[cleanZip] = parsed;
        return parsed;
      } catch (e) {}
    }

    try {
      const res = await fetch('https://api.zippopotam.us/us/' + cleanZip);
      if (res.ok) {
        const data = await res.json();
        if (data && data.places && data.places.length > 0) {
          const place = data.places[0];
          const coords = {
            lat: parseFloat(place.latitude),
            lng: parseFloat(place.longitude),
            city: place['place name'] || '',
            state: place['state abbreviation'] || place['state'] || ''
          };
          KNOWN_COORDS[cleanZip] = coords;
          localStorage.setItem(cacheKey, JSON.stringify(coords));
          return coords;
        }
      }
    } catch (err) {
      console.warn('[EMOTO ZIP Lookup Error]', err);
    }
    return null;
  }

  // Detect initial location
  async function detectInitialLocation() {
    // 1. Check saved user ZIP
    if (state.userZip) {
      state.userCoords = await resolveZipCoords(state.userZip);
    }

    // 2. If no coords yet, check theme session storage 'cc-location'
    if (!state.userCoords && window.sessionStorage && window.sessionStorage['cc-location']) {
      try {
        const stored = JSON.parse(window.sessionStorage['cc-location']);
        if (stored && stored.latitude && stored.longitude) {
          state.userCoords = { lat: stored.latitude, lng: stored.longitude };
        }
      } catch (e) {}
    }

    // 3. Fallback to default 33602
    if (!state.userCoords) {
      state.userZip = '33602';
      state.userCoords = KNOWN_COORDS['33602'];
    }

    updateZipLabels();
    recalculateAllDistances();
  }

  // Update ZIP text throughout UI
  function updateZipLabels() {
    const zipEls = document.querySelectorAll('[data-mp-user-zip]');
    zipEls.forEach(el => {
      el.textContent = state.userZip;
    });

    const filterZipLabel = document.querySelector('[data-mp-filter-zip-label]');
    if (filterZipLabel) {
      filterZipLabel.textContent = 'Distance from ' + state.userZip;
    }
  }

  // Recalculate distance for all cards on the page
  async function recalculateAllDistances() {
    if (!state.userCoords) return;

    const cards = document.querySelectorAll('.emoto-mp-card');
    for (const card of cards) {
      const targetZip = (card.dataset.zip || '').trim();
      const distanceEl = card.querySelector('.emoto-mp-card-distance');
      const distWrapper = card.querySelector('.emoto-mp-card-distance-wrap');

      if (!targetZip) {
        if (distWrapper) distWrapper.style.display = 'none';
        continue;
      }

      let targetCoords = KNOWN_COORDS[targetZip];
      if (!targetCoords) {
        targetCoords = await resolveZipCoords(targetZip);
      }

      if (targetCoords && state.userCoords) {
        const dist = calculateDistanceMiles(
          state.userCoords.lat,
          state.userCoords.lng,
          targetCoords.lat,
          targetCoords.lng
        );
        card.dataset.distance = dist != null ? dist : 999999;
        if (distanceEl) {
          distanceEl.textContent = dist + ' mi away';
        }
        if (distWrapper) {
          distWrapper.style.display = '';
        }
      } else {
        card.dataset.distance = 999999;
        if (distWrapper) distWrapper.style.display = 'none';
      }
    }

    applyFilteringAndSorting();
  }

  // Apply real-time client filtering and sorting
  function applyFilteringAndSorting() {
    const cards = Array.from(document.querySelectorAll('.emoto-mp-card'));
    let visibleCount = 0;

    cards.forEach(card => {
      let isVisible = true;

      // 1. Distance filter
      if (state.activeFilters.distance != null) {
        const dist = parseFloat(card.dataset.distance);
        if (isNaN(dist) || dist > state.activeFilters.distance) {
          isVisible = false;
        }
      }

      // 2. Condition filter
      if (isVisible && state.activeFilters.condition.length > 0) {
        const cardCond = (card.dataset.condition || '').toLowerCase();
        const matchesCond = state.activeFilters.condition.some(cond => cardCond.includes(cond.toLowerCase()));
        if (!matchesCond) isVisible = false;
      }

      // 3. Category filter
      if (isVisible && state.activeFilters.category.length > 0) {
        const cardCat = (card.dataset.category || '').toLowerCase();
        const matchesCat = state.activeFilters.category.some(cat => {
          const c = cat.toLowerCase();
          return cardCat.includes(c) || (c === 'e-bikes' && cardCat.includes('e_bike')) || (c === 'dirt bikes' && cardCat.includes('dirt_bike')) || (c === 'golf carts' && cardCat.includes('golf_cart'));
        });
        if (!matchesCat) isVisible = false;
      }

      // 4. Title / MCO filter
      if (isVisible && state.activeFilters.title.length > 0) {
        const cardTitleStatus = (card.dataset.titleStatus || '').toLowerCase();
        const matchesTitle = state.activeFilters.title.some(t => {
          const term = t.toLowerCase();
          if (term.includes('mco') || term.includes('title')) {
            return cardTitleStatus.includes('clean') || cardTitleStatus.includes('mco') || cardTitleStatus.includes('title');
          }
          if (term.includes('no paperwork')) {
            return cardTitleStatus.includes('no') || cardTitleStatus.includes('bill of sale');
          }
          return cardTitleStatus.includes(term);
        });
        if (!matchesTitle) isVisible = false;
      }

      // 5. Battery filter
      if (isVisible && state.activeFilters.battery.length > 0) {
        const cardBat = (card.dataset.batteryReplaced || '').toLowerCase();
        const matchesBat = state.activeFilters.battery.some(b => {
          const term = b.toLowerCase();
          if (term.includes('replaced')) return cardBat === 'yes' || cardBat.includes('replaced');
          if (term.includes('original')) return cardBat === 'no' || cardBat.includes('original');
          return true;
        });
        if (!matchesBat) isVisible = false;
      }

      // 6. Brand filter
      if (isVisible && state.activeFilters.brand.length > 0) {
        const cardBrand = (card.dataset.brand || '').toLowerCase();
        const matchesBrand = state.activeFilters.brand.some(b => cardBrand.includes(b.toLowerCase()));
        if (!matchesBrand) isVisible = false;
      }

      // 7. Price filter
      if (isVisible) {
        const cardPrice = parseFloat(card.dataset.price) || 0;
        if (state.activeFilters.priceMin != null && cardPrice < state.activeFilters.priceMin) {
          isVisible = false;
        }
        if (state.activeFilters.priceMax != null && cardPrice > state.activeFilters.priceMax) {
          isVisible = false;
        }
      }

      if (isVisible) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    // Update listing count
    const countEls = document.querySelectorAll('[data-mp-listing-count]');
    countEls.forEach(el => el.textContent = visibleCount);

    // Empty state
    const noResultsEl = document.querySelector('[data-mp-no-results]');
    if (noResultsEl) {
      noResultsEl.style.display = visibleCount === 0 ? '' : 'none';
      const titleEl = noResultsEl.querySelector('[data-mp-no-results-title]');
      const descEl = noResultsEl.querySelector('[data-mp-no-results-desc]');
      const actionsEl = noResultsEl.querySelector('[data-mp-no-results-actions]');
      if (cards.length > 0 && visibleCount === 0) {
        if (titleEl) titleEl.textContent = 'No matching rides found';
        if (descEl) descEl.textContent = 'Try adjusting your distance radius or clearing some filters.';
        if (actionsEl) actionsEl.innerHTML = '<button type="button" class="emoto-mp-banner-btn" onclick="document.querySelector(\'.emoto-mp-clear-all-btn\')?.click();">Clear all filters</button>';
      }
    }

    // Sort cards
    sortCards(cards);

    // Render active chips & suggestions
    renderActiveChips();
  }

  // Sort visible cards
  function sortCards(cards) {
    const grid = document.querySelector('.emoto-mp-grid');
    if (!grid) return;

    cards.sort((a, b) => {
      if (state.sort === 'nearest') {
        const distA = parseFloat(a.dataset.distance) || 999999;
        const distB = parseFloat(b.dataset.distance) || 999999;
        return distA - distB;
      }
      if (state.sort === 'price-asc') {
        const priceA = parseFloat(a.dataset.price) || 0;
        const priceB = parseFloat(b.dataset.price) || 0;
        return priceA - priceB;
      }
      if (state.sort === 'price-desc') {
        const priceA = parseFloat(a.dataset.price) || 0;
        const priceB = parseFloat(b.dataset.price) || 0;
        return priceB - priceA;
      }
      if (state.sort === 'newest') {
        const createdA = parseInt(a.dataset.created) || 0;
        const createdB = parseInt(b.dataset.created) || 0;
        return createdB - createdA;
      }
      return 0;
    });

    cards.forEach(c => grid.appendChild(c));
  }

  // Render chips bar (Active filters + Quick suggestion chips)
  function renderActiveChips() {
    const container = document.querySelector('[data-mp-active-chips]');
    if (!container) return;
    container.innerHTML = '';

    let hasActive = false;

    // 1. Distance chip
    if (state.activeFilters.distance != null) {
      hasActive = true;
      const chip = createChip('Within ' + state.activeFilters.distance + ' mi', () => {
        state.activeFilters.distance = null;
        document.querySelectorAll('input[name="filter-distance"]').forEach(cb => cb.checked = false);
        applyFilteringAndSorting();
      });
      container.appendChild(chip);
    }

    // 2. Condition, Category, Title, Battery, Brand chips
    const addArrayChips = (arr, groupName) => {
      arr.forEach(val => {
        hasActive = true;
        const chip = createChip(val, () => {
          const idx = arr.indexOf(val);
          if (idx > -1) arr.splice(idx, 1);
          document.querySelectorAll('input[name="' + groupName + '"][value="' + val + '"]').forEach(cb => cb.checked = false);
          applyFilteringAndSorting();
        });
        container.appendChild(chip);
      });
    };

    addArrayChips(state.activeFilters.condition, 'filter-condition');
    addArrayChips(state.activeFilters.category, 'filter-category');
    addArrayChips(state.activeFilters.title, 'filter-title');
    addArrayChips(state.activeFilters.battery, 'filter-battery');
    addArrayChips(state.activeFilters.brand, 'filter-brand');

    // 3. Price chip
    if (state.activeFilters.priceMin != null || state.activeFilters.priceMax != null) {
      hasActive = true;
      let label = '$' + (state.activeFilters.priceMin || '0') + ' - $' + (state.activeFilters.priceMax || 'Any');
      if (!state.activeFilters.priceMin && state.activeFilters.priceMax === 3000) {
        label = 'Under $3k';
      }
      const chip = createChip(label, () => {
        state.activeFilters.priceMin = null;
        state.activeFilters.priceMax = null;
        const minIn = document.querySelector('[data-mp-price-min]');
        const maxIn = document.querySelector('[data-mp-price-max]');
        if (minIn) minIn.value = '';
        if (maxIn) maxIn.value = '';
        applyFilteringAndSorting();
      });
      container.appendChild(chip);
    }

    // 4. Quick Suggestion Chips (as seen in mobile UI mockup)
    // "E-Bikes" suggestion
    if (!state.activeFilters.category.includes('E-Bikes')) {
      const ebikeSuggestion = createSuggestionChip('E-Bikes', () => {
        state.activeFilters.category.push('E-Bikes');
        const cb = document.querySelector('input[name="filter-category"][value="E-Bikes"]');
        if (cb) cb.checked = true;
        applyFilteringAndSorting();
      });
      container.appendChild(ebikeSuggestion);
    }

    // "Under $3k" suggestion
    if (state.activeFilters.priceMax !== 3000) {
      const priceSuggestion = createSuggestionChip('Under $3k', () => {
        state.activeFilters.priceMax = 3000;
        const maxIn = document.querySelector('[data-mp-price-max]');
        if (maxIn) maxIn.value = '3000';
        applyFilteringAndSorting();
      });
      container.appendChild(priceSuggestion);
    }

    // 5. Clear all button
    if (hasActive) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'emoto-mp-clear-all-btn';
      clearBtn.textContent = 'Clear all';
      clearBtn.addEventListener('click', clearAllFilters);
      container.appendChild(clearBtn);
    }
  }

  function createChip(text, onRemove) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'emoto-mp-chip';
    btn.innerHTML = text + ' <span class="emoto-mp-chip-x">×</span>';
    btn.addEventListener('click', onRemove);
    return btn;
  }

  function createSuggestionChip(text, onSelect) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'emoto-mp-chip emoto-mp-chip--suggestion';
    btn.textContent = text;
    btn.addEventListener('click', onSelect);
    return btn;
  }

  function clearAllFilters() {
    state.activeFilters.distance = null;
    state.activeFilters.condition = [];
    state.activeFilters.category = [];
    state.activeFilters.title = [];
    state.activeFilters.battery = [];
    state.activeFilters.brand = [];
    state.activeFilters.priceMin = null;
    state.activeFilters.priceMax = null;

    document.querySelectorAll('.emoto-mp-sidebar input[type="checkbox"]').forEach(cb => cb.checked = false);
    const minIn = document.querySelector('[data-mp-price-min]');
    const maxIn = document.querySelector('[data-mp-price-max]');
    if (minIn) minIn.value = '';
    if (maxIn) maxIn.value = '';

    applyFilteringAndSorting();
  }

  // Setup Event Listeners
  function initListeners() {
    // 1. Accordion Collapses
    document.querySelectorAll('[data-mp-accordion-header]').forEach(header => {
      header.addEventListener('click', function() {
        const group = this.closest('.emoto-mp-filter-group');
        if (group) {
          const isCollapsed = group.classList.toggle('is-collapsed');
          const icon = group.querySelector('.emoto-mp-filter-toggle-icon');
          if (icon) icon.textContent = isCollapsed ? '+' : '−';
        }
      });
    });

    // 2. Distance radio/checkbox selection
    document.querySelectorAll('input[name="filter-distance"]').forEach(input => {
      input.addEventListener('change', function() {
        if (this.checked) {
          document.querySelectorAll('input[name="filter-distance"]').forEach(other => {
            if (other !== input) other.checked = false;
          });
          state.activeFilters.distance = parseFloat(this.value);
        } else {
          state.activeFilters.distance = null;
        }
        applyFilteringAndSorting();
      });
    });

    // 3. Multi-checkbox filters (Condition, Category, Title, Battery, Brand)
    function bindCheckboxGroup(name, stateArray) {
      document.querySelectorAll('input[name="' + name + '"]').forEach(input => {
        input.addEventListener('change', function() {
          const val = this.value;
          if (this.checked) {
            if (!stateArray.includes(val)) stateArray.push(val);
          } else {
            const idx = stateArray.indexOf(val);
            if (idx > -1) stateArray.splice(idx, 1);
          }
          applyFilteringAndSorting();
        });
      });
    }

    bindCheckboxGroup('filter-condition', state.activeFilters.condition);
    bindCheckboxGroup('filter-category', state.activeFilters.category);
    bindCheckboxGroup('filter-title', state.activeFilters.title);
    bindCheckboxGroup('filter-battery', state.activeFilters.battery);
    bindCheckboxGroup('filter-brand', state.activeFilters.brand);

    // 4. Price apply button
    const priceApplyBtn = document.querySelector('[data-mp-price-apply]');
    if (priceApplyBtn) {
      priceApplyBtn.addEventListener('click', function() {
        const minVal = parseFloat(document.querySelector('[data-mp-price-min]')?.value);
        const maxVal = parseFloat(document.querySelector('[data-mp-price-max]')?.value);
        state.activeFilters.priceMin = !isNaN(minVal) ? minVal : null;
        state.activeFilters.priceMax = !isNaN(maxVal) ? maxVal : null;
        applyFilteringAndSorting();
      });
    }

    // 5. Sort selection (Both desktop and mobile)
    const sortSelects = document.querySelectorAll('[data-mp-sort-select]');
    sortSelects.forEach(select => {
      select.addEventListener('change', function() {
        state.sort = this.value;
        sortSelects.forEach(other => {
          if (other !== select) other.value = state.sort;
        });
        const cards = Array.from(document.querySelectorAll('.emoto-mp-card'));
        sortCards(cards);
      });
    });

    // 6. Mobile filter drawer toggle
    const mobileOpenBtn = document.querySelector('[data-mp-mobile-filter-open]');
    const mobileCloseBtn = document.querySelector('[data-mp-mobile-filter-close]');
    const sidebar = document.querySelector('.emoto-mp-sidebar');

    if (mobileOpenBtn && sidebar) {
      mobileOpenBtn.addEventListener('click', () => sidebar.classList.add('is-open'));
    }
    if (mobileCloseBtn && sidebar) {
      mobileCloseBtn.addEventListener('click', () => sidebar.classList.remove('is-open'));
    }

    // 7. ZIP Modal Triggers
    const zipModal = document.getElementById('emoto-zip-modal');
    const zipTriggers = document.querySelectorAll('[data-mp-zip-trigger]');
    const zipCloseBtns = document.querySelectorAll('[data-mp-zip-modal-close]');
    const zipForm = document.getElementById('emoto-zip-modal-form');
    const zipInput = document.getElementById('emoto-zip-modal-input');
    const geoBtn = document.getElementById('emoto-zip-modal-geo-btn');
    const zipStatus = document.getElementById('emoto-zip-modal-status');

    function openZipModal() {
      if (!zipModal) return;
      zipModal.classList.add('is-active');
      if (zipInput) {
        zipInput.value = state.userZip || '';
        setTimeout(() => zipInput.focus(), 100);
      }
      if (zipStatus) zipStatus.textContent = '';
    }

    function closeZipModal() {
      if (!zipModal) return;
      zipModal.classList.remove('is-active');
    }

    zipTriggers.forEach(t => t.addEventListener('click', (e) => {
      e.preventDefault();
      openZipModal();
    }));

    zipCloseBtns.forEach(b => b.addEventListener('click', (e) => {
      e.preventDefault();
      closeZipModal();
    }));

    if (zipForm) {
      zipForm.addEventListener('submit', async function(e) {
        e.preventDefault();
        const entered = (zipInput?.value || '').trim().replace(/\D/g, '').slice(0, 5);
        if (entered.length !== 5) {
          if (zipStatus) zipStatus.textContent = 'Please enter a valid 5-digit US ZIP code.';
          return;
        }

        if (zipStatus) zipStatus.textContent = 'Resolving location...';
        const coords = await resolveZipCoords(entered);
        if (coords) {
          state.userZip = entered;
          state.userCoords = coords;
          localStorage.setItem('emoto_user_zip', entered);
          updateZipLabels();
          closeZipModal();
          recalculateAllDistances();
        } else {
          if (zipStatus) zipStatus.textContent = 'Could not locate ZIP. Using fallback.';
          state.userZip = entered;
          localStorage.setItem('emoto_user_zip', entered);
          updateZipLabels();
          closeZipModal();
        }
      });
    }

    // Geolocation button inside ZIP modal
    if (geoBtn && navigator.geolocation) {
      geoBtn.addEventListener('click', function() {
        if (zipStatus) zipStatus.textContent = 'Detecting current position...';
        navigator.geolocation.getCurrentPosition(
          async function(pos) {
            const lat = pos.coords.latitude;
            const lng = pos.coords.longitude;
            state.userCoords = { lat, lng };

            // Store in sessionStorage cc-location
            try {
              sessionStorage['cc-location'] = JSON.stringify({ latitude: lat, longitude: lng });
            } catch (e) {}

            if (zipStatus) zipStatus.textContent = 'Location detected!';
            setTimeout(() => {
              closeZipModal();
              recalculateAllDistances();
            }, 500);
          },
          function(err) {
            if (zipStatus) zipStatus.textContent = 'Permission denied or unable to detect location.';
          },
          { timeout: 7000 }
        );
      });
    }

    // 8. Contact Seller Button wiring on collection cards
    setupContactSellerTriggers();
  }

  // Setup Contact Seller Modal triggers on cards
  function setupContactSellerTriggers() {
    const contactModal = document.getElementById('emoto-contact-modal');
    if (!contactModal) return;

    document.querySelectorAll('[data-mp-contact-seller]').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();

        // Populate modal with card data
        const listingCode = this.dataset.listingCode || '';
        const listingId = this.dataset.listingId || '';
        const title = this.dataset.title || '';
        const price = this.dataset.price || '';
        const image = this.dataset.image || '';
        const city = this.dataset.city || '';
        const stateLoc = this.dataset.state || '';
        const mileage = this.dataset.mileage || '';

        contactModal.dataset.listingCode = listingCode;
        contactModal.dataset.listingId = listingId;

        // Mini summary elements
        const titleEl = contactModal.querySelector('.emoto-contact-mini-title');
        const priceEl = contactModal.querySelector('.emoto-contact-mini-price');
        const metaEl = contactModal.querySelector('.emoto-contact-mini-meta');
        const imgEl = contactModal.querySelector('.emoto-contact-mini-thumb img');

        if (titleEl && title) titleEl.textContent = title;
        if (priceEl && price) priceEl.textContent = '$' + price;
        if (metaEl) {
          const parts = [];
          if (mileage) parts.push(mileage + ' mi');
          if (city && stateLoc) parts.push(city + ', ' + stateLoc);
          metaEl.textContent = parts.join(' · ');
        }
        if (imgEl && image) {
          imgEl.src = image;
          imgEl.alt = title;
        }

        // Open modal
        contactModal.classList.add('is-active');
        contactModal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        const nameInput = document.getElementById('emoto-buyer-name');
        if (nameInput) setTimeout(() => nameInput.focus(), 100);
      });
    });

    // Add to cart buttons
    document.querySelectorAll('[data-mp-add-to-cart]').forEach(btn => {
      btn.addEventListener('click', async function(e) {
        e.preventDefault();
        const variantId = this.dataset.variantId;
        if (!variantId) return;

        const originalText = this.textContent;
        this.textContent = 'Adding...';
        this.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          if (res.ok) {
            this.textContent = 'Added to cart!';
            window.location.href = '/cart';
          } else {
            this.textContent = originalText;
            this.disabled = false;
          }
        } catch (err) {
          this.textContent = originalText;
          this.disabled = false;
        }
      });
    });
  }

  // Initializer
  document.addEventListener('DOMContentLoaded', function() {
    initListeners();
    detectInitialLocation();
  });
})();
